
import vSelect from "vue-select"
import { Component, Prop, Vue, Watch, Provide, ProvideReactive, Ref } from "vue-property-decorator"
import { Getter, State, namespace } from "vuex-class"
import { CaseDetails, CaseField, TemplateDetails, Status, Severity, AuditUser } from "@/types"
import { CurrentUser } from "@/types/user"
import { CaseColumn, isBuiltInCaseColumn, getBuiltInCaseColumn } from "@/types/caseColumns"
import { TenantSettingKeys } from "@/types/tenantSetting"
import { TenantFeatures } from "@/types/tenantFeatures"
import { editableFieldTypes } from "@/types/insights"
import { inlineEditMode } from "@/types/insights/selectiveInlineEdit"
import { View } from "@/types/view"
import { cellValue } from "@/scripts/caseFields"
import { getCanUserDisableCadence } from "@/scripts/caseDetails"
import UserImage from "../UserImage.vue"
import FormGroup from "../Fields/FormGroup.vue"
import TimeSpentDisplay from "../CaseDetails/TimeSpentDisplay.vue"
import TextAreaToggleExpand from "../Fields/TextAreaToggleExpand.vue"
import DateTime from "../Fields/DateTime.vue"
import Numeric from "../Fields/Numeric.vue"
import UserRole from "../CaseDetails/Users/UserRole.vue"
import DataSourceSelect from "../Fields/DataSourceSelect.vue"
import Tags from "../Fields/Tags.vue"
import AreaSelect from "../Fields/AreaSelect.vue"
import DataSourceTemplate from "../Fields/DataSourceTemplate.vue"
import CaseFields from "../New/Cases/EditingCase/CaseFields.vue"
import { LayoutLocations } from "@/types/templateLayout"
import DataCache from "@/caching/caching"
import RecordAssignmentService from "@/services/recordAssignmentService"
import { TimeManagementSettings } from "@/types/timeManagement"
import { UserSettings, ViewsOpeningOption } from "@/types/userSettings"
import { viewsRecordLinkTargetMixin } from "@/mixins/viewsRecordLinkTargetMixin"
import CaseHubEventHandler from "../New/Cases/EditingCase/CaseHubEventHandler.vue"
import HubCaseService from "@/services/hubCaseService"

const caseStore = namespace("cases")

@Component({
	components: {
		"v-select": vSelect,
		"user-image": UserImage,
		"time-spent-display": TimeSpentDisplay,
		"text-area-expand": TextAreaToggleExpand,
		"date-time": DateTime,
		"numeric": Numeric,
		"user-role": UserRole,
		"form-group": FormGroup,
		"data-select": DataSourceSelect,
		"tags": Tags,
		"area-select": AreaSelect,
		"data-template": DataSourceTemplate,
		"case-fields": CaseFields,
		"case-hub-event-handler": CaseHubEventHandler
	},
	mixins: [viewsRecordLinkTargetMixin]
})

export default class InsightCaseSideBar extends Vue {
	/* --- Provided services --- */

	@ProvideReactive("hubCaseService") hubCaseService: HubCaseService = new HubCaseService()
	@Provide("recordAssignmentService") recordAssignmentService: RecordAssignmentService

	/* --- Refs --- */

	@Ref() caseHubEventHandler!: CaseHubEventHandler

	/* --- State --- */

	@State public userSettings!: UserSettings | null
	@State private readonly templateCache!: DataCache<TemplateDetails>
	@State timeManagementSettings!: TimeManagementSettings | null
	@Getter("getMe") public me!: CurrentUser;
	@Getter("getCurrentRouteName") public currentRouteName!: string
	@Getter("getTenantSettings") getTenantSettings!: { [key: string]: any }
	@Getter getIsFeatureEnabled!: (feature: TenantFeatures) => boolean

	@caseStore.Getter("getStatuses") private readonly statuses!: Status[]
	@caseStore.Getter("getSeverities") private readonly severities!: Severity[]

	/* --- Props --- */

	@Prop({ type: Object, default: () => null })
	view!: View | null

	@Prop({ type: Array, required: true })
	private columns!: CaseColumn[]

	@Prop({ type: Object, required: true })
	private currentCase!: CaseDetails

	@Prop({ type: Object, required: false, default: () => null })
	private currentCaseTemplate!: TemplateDetails | null

	@Prop({ type: Boolean, default: false })
	private loadingCase!: boolean

	@Prop({ type: Boolean, default: false })
	private disableNextButton!: boolean

	@Prop({ type: Boolean, default: false })
	private disablePreviousButton!: boolean

	/* --- Data --- */

	isBuiltInCaseColumn = isBuiltInCaseColumn
	getBuiltInCaseColumn = getBuiltInCaseColumn
	LayoutLocations = LayoutLocations
	private copiedCaseDetails: string = ""
	private lastUpdated: string | null = null
	private lastUpdatedUser: AuditUser | null = null

	private isMounted: boolean = false
	private isLoadingRecord: boolean = false

	private hubServiceConnectionCaseId: string | null = null
	private hubServiceConnectionPromise: Promise<unknown> | null = null

	/* --- Lifecycle hooks --- */

	constructor () {
		super()
		this.recordAssignmentService = new RecordAssignmentService(this.getIsFeatureEnabled)
	}

	private mounted () {
		this.isMounted = true
	}

	private beforeDestroy () {
		this.hubCaseService.closeConnection()
	}

	/* --- Event handlers --- */

	private async handleCaseUpdated (updatedCase: CaseDetails, updatedCaseField: CaseField | null): Promise<void> {
		this.$emit("case-updated", updatedCase, updatedCaseField)

		this.lastUpdated = new Date().toISOString()
		this.lastUpdatedUser = {
			id: this.me.id,
			name: this.me.name
		}
	}

	/* --- Methods --- */

	get isUserEditableCadence (): boolean {
		if (!this.columns.some(c => c.key === "cadenceEnabled") || this.view?.inlineEditMode === inlineEditMode.None || (this.view?.inlineEditMode === inlineEditMode.Some && !this.view?.columnOptions?.cadenceEnabled?.inlineEdit)) {
			return false
		}

		const template = this.currentCase.templateId
			? this.templateCache.getById(this.currentCase.templateId!)
			: null

		return template ? getCanUserDisableCadence(this.currentCase, this.me, template, this.getIsFeatureEnabled) : false
	}

	private isFieldEditable (column: CaseColumn): boolean {
		if (!this.isMounted) return false

		return this.editableColumns[column.key] &&
			this.columnValue[column.key] &&
			this.columnValue[column.key].fieldType &&
			(!isBuiltInCaseColumn(column.key) || this.isBuiltInFieldEditableOnCase(column)) &&
			(!this.currentCase.closed || this.templatesEditableAfterClose.has(this.currentCase.templateId!))
	}

	private isBuiltInFieldEditableOnCase (column: CaseColumn): boolean {
		if (!this.currentCase) return false

		const columnDefinition = getBuiltInCaseColumn(column.key) as CaseColumn

		if (!columnDefinition) {
			throw new Error(`Column ${column.key} is not a built-in field`)
		}

		if (columnDefinition.isReadonly) return false
		if (columnDefinition.adminOnly && !this.me.isAdmin) return false

		var editable = true
		switch (column.key) {
			case "pendingTriage":
				editable = false
				break
			case "title":
				editable = !this.currentCase.titleTemplateFormat
				break
			case "cadenceEnabled":
				editable = !this.currentCase.closed && this.isUserEditableCadence
				break
		}

		return editable
	}

	public async copyCaseDetails () {
		if (this.currentCase.caseNumber && this.currentCase.title) {
			let entityIdentifier = this.currentCase.caseNumber
			let encodedEntityIdentifier = encodeURIComponent(entityIdentifier)
			let endPath = "cases"

			let routeData = this.$router.resolve({ path: `/v2/${endPath}/${encodedEntityIdentifier}` })
			let basePath = window.location.origin + window.location.pathname.replace(/\/$/, "")
			let url = `${basePath}/${routeData.href}`

			const caseDetails = `Title: ${this.currentCase.title}\n${this.caseNameLabel}: #${this.currentCase.caseNumber}\nURL: ${url}`
			const html = `<p>Title: ${this.currentCase.title}</p><p>${this.caseNameLabel}: #${this.currentCase.caseNumber}</p><p>URL: <a href="${url}"> ${url}</a></p>`

			const type = "text/html"
			const blob = new Blob([html], { type: "text/html" })
			const blob2 = new Blob([caseDetails], { type: "text/plain" })

			try {
				// @ts-ignore
				await navigator.clipboard.write([new ClipboardItem({ [type]: blob, "text/plain": blob2 })])
				this.copiedCaseDetails = "Copied"
			} catch (err) {
				console.error("Failed to copy: ", err)
			}
		}
	}

	/* --- Vue-related functions --- */

	@Watch("editableFieldIds", { immediate: true, deep: true })
	onEditableFieldIdsChange (newVal: string[], oldVal: string[]) {
		this.$emit("editable-field-ids-changed", newVal)
	}

	@Watch("currentCase", { immediate: true })
	async onCurrentCaseChange (newCase: CaseDetails): Promise<void> {
		this.lastUpdated = null
		this.lastUpdatedUser = null
		this.recordAssignmentService.clear()

		const assignmentServiceLoadPromise = this.recordAssignmentService.load(newCase, this.getIsFeatureEnabled)
			.catch(err => {
				console.error("Failed to load case using record assignment service:", err)
			})

		if (newCase?.id) {
			if (newCase.id !== this.hubServiceConnectionCaseId) {
				this.openHubConnection(newCase.id)
			}
		} else {
			this.closeHubConnection()
		}

		await Promise.allSettled([assignmentServiceLoadPromise, this.hubServiceConnectionPromise])
	}

	@Watch("loadingCase", { immediate: true })
	onLoadingCaseChange (newValue: boolean) {
		if (newValue) {
			setTimeout(() => {
				if (this.loadingCase) {
					this.isLoadingRecord = true
				}
			}, 400)
		} else {
			this.isLoadingRecord = false
		}
	}

	get userTeamsIds (): Set<string> {
		if (!this.me || !this.me.teams) {
			return new Set()
		}
		return new Set(this.me.teams.map(t => t.id))
	}

	get showConsolidatedInfo (): boolean {
		return !!this.currentCase?.id
	}

	get caseNameLabel (): string {
		return this.currentCase?.isCase ? "Case" : "Record"
	}

	get createdDetailsLabel (): string {
		return this.currentCase?.createdBy ? "Created By: " : "Created at: "
	}

	get editDetailsLabel (): string {
		return this.currentCase?.lastUserUpdatedBy || this.lastUpdatedUser ? "Last Edit By: " : "Last Edit at: "
	}

	get closedDetailsLabel (): string {
		return this.currentCase?.closedBy ? "Closed By: " : "Closed at: "
	}

	get fieldEditingEnabled (): boolean {
		return !!(this.getTenantSettings?.[TenantSettingKeys.AllowFieldEditingInViews])
	}

	get pageAllowsEdits (): boolean {
		return this.currentRouteName === "dashboard" ||
			this.currentRouteName === "overview" || // views
			this.currentRouteName === "v2-cases-overview" || // views in new interface
			this.currentRouteName === "insights"
	}

	get columnValue (): { [key: string]: any } {
		return this.columns.reduce((values: { [key: string]: any }, column) => {
			values[column.key] = cellValue(column, this.currentCase)
			return values
		}, {})
	}

	get editableColumns (): { [key: string]: boolean } {
		if (!this.isMounted || !this.pageAllowsEdits || !this.fieldEditingEnabled ||
			!this.getIsFeatureEnabled(TenantFeatures.InlineEditByRow) ||
			(this.getIsFeatureEnabled(TenantFeatures.SelectiveInlineEdit) && this.view?.inlineEditMode === inlineEditMode.None)
		) {
			return {}
		}

		let editableColumns = this.columns.reduce((values: { [key: string]: boolean }, column) => {
			let inlineEditAvailable = !this.getIsFeatureEnabled(TenantFeatures.SelectiveInlineEdit) || this.view?.inlineEditMode === inlineEditMode.All || this.view?.columnOptions?.[column.key]?.inlineEdit
			if (inlineEditAvailable &&
				(column.dynamicField || (this.getIsFeatureEnabled(TenantFeatures.BuiltInFieldsInlineEdit) && !column.isReadonly)) &&
				editableFieldTypes.has(column.dataType) && !column.key?.startsWith("calculation.")
			) {
				values[column.key] = true
			}
			return values
		}, {})

		return editableColumns
	}

	get columnFieldMap (): Record<string, { fieldToEdit: CaseField | null; fieldId: string }> {
		return this.columns.reduce((acc, column) => {
			const fieldId = column.key.startsWith("fields.")
				? column.key.slice("fields.".length)
				: column.key

			const fieldToEdit = !this.isBuiltInCaseColumn(column.key)
				? this.currentCase.fields[fieldId]
				: null

			acc[column.key] = {
				fieldToEdit,
				fieldId
			}

			return acc
		}, {} as Record<string, { fieldToEdit: CaseField | null; fieldId: string }>)
	}

	get editableFieldIds (): string[] {
		if (!this.isMounted) return []

		return this.columns
			.filter(column => this.isFieldEditable(column))
			.map(column => this.columnFieldMap[column.key]?.fieldId)
			.filter(fieldId => fieldId !== null) as string[]
	}

	get hasEditableFields (): boolean {
		return this.editableFieldIds && this.editableFieldIds.length > 0
	}

	get hiddenFieldIds (): string[] {
		const hiddenFieldIds: string[] = []

		if (!this.editableFieldIds || !this.currentCase || !this.currentCase.fields || !this.currentCaseTemplate || !this.currentCaseTemplate.sectionDetails) {
			return hiddenFieldIds
		}

		for (const editableFieldId of this.editableFieldIds) {
			const caseField = this.currentCase.fields[editableFieldId]
			if (!caseField || !caseField.section) {
				continue
			}

			for (const sectionKey in this.currentCaseTemplate.sectionDetails) {
				if (this.currentCaseTemplate.sectionDetails.hasOwnProperty(sectionKey)) {
					const sectionDetail = this.currentCaseTemplate.sectionDetails[sectionKey]

					if (sectionDetail.title === caseField.section && sectionDetail.layout === LayoutLocations.Hidden) {
						hiddenFieldIds.push(editableFieldId)
						break
					}
				}
			}
		}
		return hiddenFieldIds
	}

	get templatesEditableAfterClose (): Set<string> {
		if (!this.view || this.view.inlineEditMode === inlineEditMode.None) {
			return new Set<string>()
		}

		const templates = this.templateCache.items

		return new Set<string>(templates.filter(template => {
			return template.editClosedPermissions?.some(teamId => this.userTeamsIds.has(teamId))
		}).map(record => record.id!))
	}

	private get isLoading (): boolean {
		return !this.isMounted || this.isLoadingRecord
	}

	private async openHubConnection (recordId: string): Promise<unknown> {
		try {
			await this.closeHubConnection()
			this.hubServiceConnectionCaseId = recordId
			this.hubServiceConnectionPromise = this.hubCaseService.openConnection(recordId)
				.then((connection) => {
					if (connection && this.caseHubEventHandler) {
						this.caseHubEventHandler.handleHubRecordEvents(connection)
					}
				})
		} catch (error) {
			console.error("Failed to open case hub connection:", error)
		}

		return this.hubServiceConnectionPromise
	}

	private async closeHubConnection () {
		await this.hubCaseService.closeConnection()
		this.hubServiceConnectionCaseId = null
	}
}
